import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import "../../styles/manage.css";

const API_BASE_URL = 'https://api1.ororigin.xyz';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [signature, setSignature] = useState('');
  const fileInputRef = useRef(null);

  const uid = Cookies.get('uid');

  // 获取用户信息
  const fetchUserInfo = async () => {
    if (!uid) {
      setError('用户未登录');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/${uid}`);
      if (response.data.success) {
        setUser(response.data.user);
        
        // 从cookie读取个人签名
        const savedSignature = Cookies.get(`signature_${uid}`);
        if (savedSignature) {
          setSignature(savedSignature);
        }
      } else {
        setError(response.data.message || '获取用户信息失败');
      }
    } catch (err) {
      console.error('获取用户信息失败:', err);
      setError('获取用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新用户名
  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      setUsernameError('用户名不能为空');
      return;
    }

    if (!uid) {
      setUsernameError('用户未登录');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/update-username`, {
        uid,
        username: newUsername
      });

      if (response.data.success) {
        setUser(response.data.user);
        setNewUsername('');
        setShowUsernameForm(false);
        setUsernameError('');
        
        // 更新cookie中的用户名显示
        Cookies.set('username', newUsername);
      } else {
        setUsernameError(response.data.message);
      }
    } catch (err) {
      console.error('更新用户名失败:', err);
      if (err.response?.data?.message) {
        setUsernameError(err.response.data.message);
      } else {
        setUsernameError('更新用户名失败');
      }
    }
  };

  // 上传头像
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uid || !user?.username) {
      return;
    }

    // 验证文件类型
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('不支持的文件类型，请上传图片文件 (png, jpg, jpeg, gif, webp)');
      return;
    }

    // 验证文件大小（2MB）
    if (file.size > 2 * 1024 * 1024) {
      setError('文件大小不能超过2MB');
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('uid', uid);
    formData.append('username', user.username);

    setUploadingAvatar(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/upload-avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUser(response.data.user);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('上传头像失败:', err);
      setError('上传头像失败');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      window.location.reload()
    }
  };

  // 保存个人签名到cookie
  const handleSaveSignature = () => {
    if (uid) {
      Cookies.set(`signature_${uid}`, signature, { expires: 365 });
      // 显示成功提示
      alert('个人签名已保存');
    }
  };

  // 时间组件
  const TimeWidget = () => {
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);

      return () => clearInterval(timer);
    }, []);

    const formatTime = (date) => {
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };

    return (
      <div className="userprofile-widget userprofile-time-widget userprofile-glass-effect">
        <h3>当前时间</h3>
        <div className="userprofile-time-display">
          {formatTime(currentTime)}
        </div>
      </div>
    );
  };

  // 初始化
  useEffect(() => {
    fetchUserInfo();
  }, []);

  if (loading) {
    return (
      <div className="manage-content">
        <div className="userprofile-loading-container">
          <div className="userprofile-loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-content">
        <div className="userprofile-error-container">
          <p className="userprofile-error-message">{error}</p>
          <button 
            className="userprofile-retry-button"
            onClick={fetchUserInfo}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="manage-content">
        <div className="userprofile-no-user-container">
          <p>未找到用户信息</p>
        </div>
      </div>
    );
  }

  return (
      <div className="userprofile-profile-container">
        {/* 用户信息主块 */}
        <div className="userprofile-profile-main userprofile-glass-effect">
          <div className="userprofile-avatar-section">
            <div className="userprofile-avatar-wrapper">
              <img 
                src={`https://api1.ororigin.xyz/api/user/avatar/${uid}`} 
                alt="用户头像"
                className="userprofile-user-avatar"
                onError={(e) => {
                  e.target.src = `${API_BASE_URL}/default.png`;
                }}
              />
              <div className="userprofile-avatar-overlay">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="userprofile-avatar-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? '上传中...' : '更换头像'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="userprofile-user-info-section">
            <div className="userprofile-username-display">
              <h1 className="userprofile-username">{user.username}</h1>
              {!user.is_reserved && (
                <button
                  className="userprofile-edit-username-button"
                  onClick={() => setShowUsernameForm(!showUsernameForm)}
                >
                  修改用户名
                </button>
              )}
            </div>
            
            <div className="userprofile-user-details">
              <p><strong>UID:</strong> {user.uid}</p>
              <p><strong>创建时间:</strong> {new Date(user.created_at).toLocaleString()}</p>
              <p><strong>最后登录:</strong> {user.last_login ? new Date(user.last_login).toLocaleString() : '从未登录'}</p>
              <p><strong>状态:</strong> 
                <span className={`userprofile-status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? '活跃' : '未激活'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* 修改用户名表单 */}
        {showUsernameForm && (
          <div className="userprofile-username-form-container userprofile-glass-effect">
            <form onSubmit={handleUpdateUsername} className="userprofile-username-form">
              <div className="userprofile-form-group">
                <label htmlFor="newUsername">新用户名:</label>
                <input
                  type="text"
                  id="newUsername"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="请输入新用户名"
                  className="userprofile-username-input"
                />
                {usernameError && (
                  <p className="userprofile-error-message">{usernameError}</p>
                )}
              </div>
              <div className="userprofile-form-actions">
                <button type="submit" className="userprofile-submit-button">
                  确认修改
                </button>
                <button
                  type="button"
                  className="userprofile-cancel-button"
                  onClick={() => {
                    setShowUsernameForm(false);
                    setUsernameError('');
                    setNewUsername('');
                  }}
                >
                  取消
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 小组件区域 */}
        <div className="userprofile-widgets-container">
          {/* 个人签名组件 */}
          <div className="userprofile-widget userprofile-signature-widget userprofile-glass-effect">
            <h3>个人签名</h3>
            <textarea
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder=""
              className="userprofile-signature-input"
              rows="4"
            />
            <button
              className="userprofile-save-signature-button"
              onClick={handleSaveSignature}
            >
              保存签名
            </button>
          </div>

          {/* 账户状态组件 */}
          <div className="userprofile-widget userprofile-status-widget userprofile-glass-effect">
            <h3>账户状态</h3>
            <div className="userprofile-status-items">
              <div className="userprofile-status-item">
                <span className="userprofile-status-label">已绑定:</span>
                <span className={`userprofile-status-value ${user.is_bound ? 'yes' : 'no'}`}>
                  {user.is_bound ? '是' : '否'}
                </span>
              </div>
              <div className="userprofile-status-item">
                <span className="userprofile-status-label">活跃状态:</span>
                <span className={`userprofile-status-value ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? '活跃' : '未激活'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default UserProfile;